import { Add, RemoveCircleOutline } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import useMyDropdowns from 'hooks/useMyDropdowns';
import { IRecordFieldWithFieldName, IRoleAccess } from 'ui-component/records/types';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import SectionField from 'views/DataFocusView/components/SectionField';
import { FieldConditional, FormSectionField, FormSectionWithChildren } from 'views/DataFocusView/types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { StyledBox, StyledTitle } from './components';

export type ShipmentsSectionProps = {
    section: FormSectionWithChildren;
    filteredSectionFields: (FormSectionField & {
        order: number;
    })[];
    forcedWidth?: number;
    headers: Record<string, IRecordFieldWithFieldName>;
    listValuesByListId: Record<number, ListValue[]>;
    propertiesById: Record<number, ObjectProperty>;
    fullfilledConditionsIds?: string[];
    fieldAccessList: IRoleAccess[];
    activeDataFocusView?: boolean;
    loadingListValues?: boolean;
    inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    getFieldThenOrOtherwiseConditions: (fieldName: string, objectPropertyName?: string) => FieldConditional[] | undefined;
};

const ShipmentsSection = ({
    section,
    filteredSectionFields,
    forcedWidth,
    headers,
    listValuesByListId,
    propertiesById,
    fieldAccessList,
    fullfilledConditionsIds,
    activeDataFocusView,
    loadingListValues,
    inputRefs,
    getFieldThenOrOtherwiseConditions
}: ShipmentsSectionProps) => {
    const shipmentsRecordTypeId = section.recordType?.id;
    const shipmentsRecordTypeName = section.recordType?.name;

    const { control } = useFormContext();
    const {
        fields: shippingRows,
        append,
        remove
    } = useFieldArray({
        control,
        name: shipmentsRecordTypeName
    });

    const { problemCodeList, recordStatusList, usersList, targetTenantList } = useMyDropdowns({
        recordTypeId: +shipmentsRecordTypeId
    });

    const addNewShippingRow = () => {
        const newRow = filteredSectionFields?.reduce(
            (acc, curField) => ({ ...acc, [`${curField?.baseField || curField?.name}`]: curField?.defaultValue || '' }),
            {}
        );
        append(newRow);
    };

    return (
        <StyledBox>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <StyledTitle>Shipments</StyledTitle>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Typography>
                        Total:
                        <Box component="span" fontWeight="bold" ml="5px">
                            {shippingRows.length}
                        </Box>
                    </Typography>
                    <Add sx={{ backgroundColor: '#EBEEFE', borderRadius: '50%', cursor: 'pointer' }} onClick={addNewShippingRow} />
                </Box>
            </Box>
            {shippingRows.map((_, idx) => (
                <StyledBox key={idx}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <StyledTitle>{`Shipment #${idx + 1}`}</StyledTitle>
                        <RemoveCircleOutline sx={{ borderRadius: '50%', cursor: 'pointer' }} onClick={() => remove(idx)} />
                    </Box>
                    <Grid container spacing={1} sx={{ maxWidth: forcedWidth ? `calc(95vw - ${forcedWidth}px)` : '100%' }}>
                        {filteredSectionFields.map((filteredField) => (
                            <SectionField
                                key={`${section.id}-${shipmentsRecordTypeName}.${idx}.${filteredField.name}`}
                                filteredField={{ ...filteredField, nestedFieldPath: `${shipmentsRecordTypeName}.${idx}` }}
                                nestedField
                                section={section}
                                headers={headers}
                                propertiesById={propertiesById}
                                listValuesByListId={listValuesByListId}
                                recordStatusList={recordStatusList}
                                problemCodeList={problemCodeList}
                                usersList={usersList}
                                targetTenantList={targetTenantList}
                                fieldAccessList={fieldAccessList}
                                fullfilledConditionsIds={fullfilledConditionsIds}
                                activeDataFocusView={activeDataFocusView}
                                loadingListValues={loadingListValues}
                                inputRefs={inputRefs}
                                getFieldThenOrOtherwiseConditions={getFieldThenOrOtherwiseConditions}
                            />
                        ))}
                    </Grid>
                </StyledBox>
            ))}
        </StyledBox>
    );
};
export default ShipmentsSection;
